import { createSelector } from 'reselect';
import { customersAdapterSelectors } from '../redux/slices/customers';
import { getUser } from './userSelectors';

const getCurrentWorkspaceCustomersFromStore = (state) =>
  state.currentWorkspace.customers;

export const getAll = createSelector(
  getCurrentWorkspaceCustomersFromStore,
  (state) => (id) => customersAdapterSelectors.selectById(state, id),
  (customerIds, getCustomerById) =>
    customerIds.map((customerId) => getCustomerById(customerId))
);

export const currentWorkspaceCustomers = createSelector(
  (state) => customersAdapterSelectors.selectAll(state),
  (state) => state.currentWorkspace.id,
  (customers, currentWorkspace) =>
    customers.filter((customer) => customer.workspaceId === currentWorkspace)
);

export const selectCustomerById = createSelector(
  (state) => customersAdapterSelectors.selectAll(state),
  (_, customerId) => customerId,
  (customers, customerId) =>
    customers.filter((customer) => customer.id === customerId)
);

export const selectCustomersByMembershipAccess = createSelector(
  (state) => customersAdapterSelectors.selectAll(state),
  getUser(),
  (customers, user) => {
    if (!user) return null;

    const customerIds = user.userRoles?.reduce((acc, currentValue) => {
      if (currentValue.customer) {
        acc.push(currentValue.customer.id);
      } else if (currentValue.device?.customer) {
        acc.push(currentValue.device.customer.id);
      }
      return acc;
    }, []);

    const customerMember = customers.filter((customer) =>
      customerIds.includes(customer.id)
    );
    return customerMember;
  }
);

// True when the user holds at least one device-scoped role and no broader
// grant (customer/workspace/account/system). The USER-scope self role every
// account carries (all scope fields null) is not a broader grant, so it must
// not disqualify. Derived from the roles themselves — never from
// ui.isLoggedAsCustomer, which is a view toggle the user can flip from the
// account menu.
//
// Device-limited FOR THIS customer: the user reaches it only through a device
// role, with no broader grant (workspace/account/system) and no customer role
// ON THIS customer. A customer role on a DIFFERENT customer must not clear it —
// gating account-wide was the bug that leaked the role picker and pending
// invitations into a device-scoped view.
export const selectIsDeviceLimitedForCustomer = createSelector(
  getUser(),
  (_, customerId) => customerId,
  (user, customerId) => {
    const roles = user?.userRoles ?? [];
    const hasBroaderOrCustomerGrant = roles.some(
      (role) =>
        role.workspace ||
        role.account ||
        role.systemId ||
        role.customer?.id === customerId
    );
    return (
      !hasBroaderOrCustomerGrant &&
      roles.some((role) => role.device?.customerId === customerId)
    );
  }
);

// Can the current user manage members of THIS customer? True for broader-scope
// holders (workspace/account/system) or a customer-scoped role on this customer.
// Device-only access to a customer cannot manage — those actions authorize at
// workspace/customer scope and always 403. The backend enforces the boundary;
// this only hides buttons that would fail.
export const selectCanManageCustomerMembers = createSelector(
  getUser(),
  (_, customerId) => customerId,
  (user, customerId) => {
    const roles = user?.userRoles ?? [];
    return roles.some(
      (role) =>
        role.workspace ||
        role.account ||
        role.systemId ||
        role.customer?.id === customerId
    );
  }
);
